import React, { CSSProperties } from 'react';
import { toJsxRuntime } from 'hast-util-to-jsx-runtime';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faHashtag } from '@crisp/crisp-ui/icons/classic/regular';
import {
  Card,
  CardGroup,
  Accordion,
  AccordionGroup,
  Callout,
  Info,
  Note,
  Tip,
  Warning,
  Check,
  Steps,
  Step,
  Tabs,
  Tab,
  Frame,
  Expandable,
  CodeBlock,
  Icon,
  ContentImage,
} from '../components/mdx';

/**
 * Normalizes inline style property which might be a string or object.
 */
function normalizeStyle(style: unknown): CSSProperties | undefined {
  if (!style) return undefined;
  if (typeof style === 'object') return style as CSSProperties;
  if (typeof style === 'string') {
    const obj: Record<string, string> = {};
    style.split(';').forEach((rule) => {
      const colonIdx = rule.indexOf(':');
      if (colonIdx !== -1) {
        const rawKey = rule.slice(0, colonIdx).trim();
        const rawVal = rule.slice(colonIdx + 1).trim();
        if (rawKey && rawVal) {
          const camelKey = rawKey.replace(/-([a-z0-9])/g, (_, g) => g.toUpperCase());
          obj[camelKey] = rawVal;
        }
      }
    });
    return obj as CSSProperties;
  }
  return undefined;
}

const BLOCK_PARAGRAPH_PROP = 'data-block-paragraph';

// Elements allowed inside a <p>. Anything else (tables, nested paragraphs, MDX
// callouts like <Note>) is invalid there and trips React's nesting check.
const PHRASING_TAGS = new Set([
  'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'button', 'cite', 'code', 'data', 'del', 'dfn', 'em',
  'font', 'i', 'img', 'input', 'ins', 'kbd', 'label', 'mark', 'q', 's', 'samp', 'small', 'span',
  'strong', 'sub', 'sup', 'svg', 'time', 'u', 'var', 'wbr', 'Icon', 'icon',
]);

interface HastLike {
  type?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastLike[];
}

function containsBlockContent(nodes: HastLike[] = []): boolean {
  return nodes.some(
    (child) =>
      child.type === 'element' &&
      (!PHRASING_TAGS.has(child.tagName || '') || containsBlockContent(child.children))
  );
}

/** Flags paragraphs holding block content so the `p` component renders them as a div. */
export function markBlockParagraphs(node: unknown): unknown {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map(markBlockParagraphs);

  const current = node as HastLike;
  const children = Array.isArray(current.children)
    ? current.children.map((child) => markBlockParagraphs(child) as HastLike)
    : current.children;
  const isBlockParagraph =
    current.type === 'element' && current.tagName === 'p' && containsBlockContent(children);

  return {
    ...current,
    ...(isBlockParagraph
      ? { properties: { ...current.properties, [BLOCK_PARAGRAPH_PROP]: true } }
      : {}),
    ...(children ? { children } : {}),
  };
}

// Heading Anchor Helper
const HeadingWithAnchor = ({
  level,
  id,
  children,
}: {
  level: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  id?: string;
  children: React.ReactNode;
}) => {
  const Tag = level;
  return (
    <Tag id={id}>
      <span>{children}</span>
      {id && (
        <a href={`#${id}`} aria-label={`Link to ${id}`} className="heading-anchor">
          <FontAwesomeIcon icon={faHashtag} className="icon-slot" />
        </a>
      )}
    </Tag>
  );
};

export const defaultComponents: Record<string, React.ComponentType<any>> = {
  // MDX Components (PascalCase)
  Card,
  CardGroup,
  Accordion,
  AccordionGroup,
  Callout,
  Info,
  Note,
  Tip,
  Warning,
  Check,
  Steps,
  Step,
  Tabs,
  Tab,
  Frame,
  Expandable,
  CodeBlock,
  Icon,

  // MDX Components (lowercase)
  card: Card,
  cardgroup: CardGroup,
  accordion: Accordion,
  accordiongroup: AccordionGroup,
  callout: Callout,
  info: Info,
  note: Note,
  tip: Tip,
  warning: Warning,
  check: Check,
  steps: Steps,
  step: Step,
  tabs: Tabs,
  tab: Tab,
  frame: Frame,
  expandable: Expandable,
  codeblock: CodeBlock,
  icon: Icon,

  // HTML Headings
  h1: ({ id, children }) => (
    <HeadingWithAnchor level="h1" id={id}>
      {children}
    </HeadingWithAnchor>
  ),
  h2: ({ id, children }) => (
    <HeadingWithAnchor level="h2" id={id}>
      {children}
    </HeadingWithAnchor>
  ),
  h3: ({ id, children }) => (
    <HeadingWithAnchor level="h3" id={id}>
      {children}
    </HeadingWithAnchor>
  ),
  h4: ({ id, children }) => (
    <HeadingWithAnchor level="h4" id={id}>
      {children}
    </HeadingWithAnchor>
  ),
  h5: ({ id, children }) => (
    <HeadingWithAnchor level="h5" id={id}>
      {children}
    </HeadingWithAnchor>
  ),
  h6: ({ id, children }) => (
    <HeadingWithAnchor level="h6" id={id}>
      {children}
    </HeadingWithAnchor>
  ),

  p: ({ children, className = '', style, [BLOCK_PARAGRAPH_PROP]: hasBlockContent }: any) => {
    const Tag = hasBlockContent ? 'div' : 'p';
    return (
      <Tag style={normalizeStyle(style)} className={className}>
        {children}
      </Tag>
    );
  },

  a: ({ href, children, className = '', ...props }: any) => {
    if (!href) {
      return (
        <span className={className} {...props}>
          {children}
        </span>
      );
    }

    const isExternal =
      href.startsWith('http://') ||
      href.startsWith('https://') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:');

    if (isExternal) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...props}>
          <span>{children}</span>
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            className="icon-slot external-link-icon"
          />
        </a>
      );
    }

    if (href.startsWith('#')) {
      return (
        <a href={href} className={className} {...props}>
          {children}
        </a>
      );
    }

    return (
      <Link to={href} className={className} {...props}>
        {children}
      </Link>
    );
  },

  ul: ({ children, className = '' }) => <ul className={className}>{children}</ul>,
  ol: ({ children, className = '' }) => <ol className={className}>{children}</ol>,
  li: ({ children, className = '' }) => <li className={className}>{children}</li>,

  blockquote: ({ children, className = '' }) => (
    <blockquote className={className}>{children}</blockquote>
  ),

  // Code & Pre
  pre: ({ children, className = '', ...props }: any) => {
    let codeChild: React.ReactElement | null = null;
    let codeClassName = '';

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const childProps = child.props as any;
        const cls = childProps?.className;
        const clsStr = Array.isArray(cls) ? cls.join(' ') : typeof cls === 'string' ? cls : '';
        if (child.type === 'code' || child.type === defaultComponents.code || clsStr.includes('lang')) {
          codeChild = child;
          codeClassName = clsStr;
        }
      }
    });

    const preClsStr = Array.isArray(className) ? className.join(' ') : typeof className === 'string' ? className : '';
    const combinedClassName = `${preClsStr} ${codeClassName}`.trim();

    if (codeChild) {
      const codeProps = (codeChild as React.ReactElement).props as any;
      return (
        <CodeBlock className={combinedClassName}>
          {codeProps.children}
        </CodeBlock>
      );
    }

    return <CodeBlock className={combinedClassName}>{children}</CodeBlock>;
  },
  code: ({ children, className = '', ...props }: any) => {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },

  table: ({ children, className = '', style, ...props }: any) => {
    return (
      <div className="table-wrap">
        <table style={normalizeStyle(style)} className={className} {...props}>
          {children}
        </table>
      </div>
    );
  },
  thead: ({ children, className = '', style, ...props }: any) => (
    <thead style={normalizeStyle(style)} className={className} {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, className = '', style, ...props }: any) => (
    <tbody style={normalizeStyle(style)} className={className} {...props}>
      {children}
    </tbody>
  ),
  tr: ({ children, className = '', style, ...props }: any) => (
    <tr style={normalizeStyle(style)} className={className} {...props}>
      {children}
    </tr>
  ),
  th: ({ children, className = '', style, align, ...props }: any) => (
    <th
      style={{ ...normalizeStyle(style), textAlign: align || undefined }}
      className={className}
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, className = '', style, align, ...props }: any) => (
    <td
      style={{ ...normalizeStyle(style), textAlign: align || undefined }}
      className={className}
      {...props}
    >
      {children}
    </td>
  ),

  img: ({ src, alt, className = '', style, ...props }) => (
    <ContentImage
      src={src}
      alt={alt || ''}
      className={className}
      style={normalizeStyle(style)}
      {...props}
    />
  ),

  hr: () => <hr />,
};

interface RenderHastProps {
  hast: unknown;
  customComponents?: Record<string, React.ComponentType<any>>;
  routePrefix?: string;
}

export function prefixInternalLinks(node: unknown, routePrefix: string): unknown {
  if (!routePrefix || !node || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map((child) => prefixInternalLinks(child, routePrefix));

  const current = node as Record<string, unknown>;
  const properties =
    current.properties && typeof current.properties === 'object'
      ? { ...(current.properties as Record<string, unknown>) }
      : undefined;
  if (properties && typeof properties.href === 'string') {
    const href = properties.href;
    if (href.startsWith('/') && href !== routePrefix && !href.startsWith(`${routePrefix}/`)) {
      properties.href = `${routePrefix}${href}`;
    }
  }

  return {
    ...current,
    ...(properties ? { properties } : {}),
    ...(Array.isArray(current.children)
      ? { children: current.children.map((child) => prefixInternalLinks(child, routePrefix)) }
      : {}),
  };
}

/**
 * Renders a HAST tree to React nodes using hast-util-to-jsx-runtime and Mintlify component suite.
 */
export const HastRenderer: React.FC<RenderHastProps> = ({
  hast,
  customComponents = {},
  routePrefix = '',
}) => {
  if (!hast) return null;

  try {
    const components = {
      ...defaultComponents,
      ...customComponents,
    };

    return (
      <div className="crisp-prose">
        {toJsxRuntime(
          markBlockParagraphs(prefixInternalLinks(hast, routePrefix)) as Parameters<typeof toJsxRuntime>[0],
          {
            Fragment,
            jsx,
            jsxs,
            components,
          }
        )}
      </div>
    );
  } catch (error) {
    console.error('Error rendering HAST content:', error);
    return (
      <div className="render-error">
        <p>
          <strong>Failed to render content</strong>
        </p>
        <p>{String(error)}</p>
      </div>
    );
  }
};

export default HastRenderer;
